import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Button,
  Card,
  Icon,
  MoodChip,
  StreakPill,
  moodById,
  type MoodId,
} from "@wiseowl/design-system";
import { isApiError, type ReflectionListItem } from "@wiseowl/shared";
import { api } from "../lib/api.js";
import type { AppShellContext } from "../layouts/app-layout.js";

/*
 * Reflect — the personal dashboard. A reading surface for one's own
 * practice: streak, recent reflections preview, and mood status. The
 * reflection *flow* is still launched as an overlay (openReflect).
 */
interface MoodEntry {
  id: string;
  moodSlug: string;
  createdAt: string;
}

const relativeDay = (iso: string): string => {
  const then = new Date(iso);
  const days = Math.floor((Date.now() - then.getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return then.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const Reflect = () => {
  const { openReflect } = useOutletContext<AppShellContext>();
  const [streak, setStreak] = useState<number>(0);
  const [longest, setLongest] = useState<number>(0);
  const [reflections, setReflections] = useState<ReflectionListItem[]>([]);
  const [moods, setMoods] = useState<MoodEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [s, r, m] = await Promise.all([
        api<{ streak: { currentDays: number; longestDays: number } }>("/streak?practice=daily-reset"),
        api<{ reflections: ReflectionListItem[] }>("/reflections?limit=5"),
        api<{ moods: MoodEntry[] }>("/moods?limit=30"),
      ]);
      if (cancelled) return;
      if (!isApiError(s)) { setStreak(s.data.streak.currentDays); setLongest(s.data.streak.longestDays); }
      if (!isApiError(r)) setReflections(r.data.reflections);
      if (!isApiError(m)) setMoods(m.data.moods);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Top moods over the recent window.
  const moodCounts = moods.reduce<Record<string, number>>((acc, m) => {
    acc[m.moodSlug] = (acc[m.moodSlug] ?? 0) + 1;
    return acc;
  }, {});
  const topMoods = Object.entries(moodCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
            Your practice
          </div>
          <h1 className="mt-1 font-serif text-display font-medium leading-tight tracking-tight text-ink">
            Reflect
          </h1>
        </div>
        <StreakPill days={streak} />
      </header>

      {/* Status row */}
      <section className="grid gap-3 sm:grid-cols-3">
        <Card surface="cream-2">
          <div className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">Current streak</div>
          <div className="mt-1 font-serif text-h1 font-medium text-ink">{streak} {streak === 1 ? "day" : "days"}</div>
        </Card>
        <Card surface="cream-2">
          <div className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">Longest streak</div>
          <div className="mt-1 font-serif text-h1 font-medium text-ink">{longest} {longest === 1 ? "day" : "days"}</div>
        </Card>
        <Card surface="cream-2">
          <div className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">Reflections</div>
          <div className="mt-1 font-serif text-h1 font-medium text-ink">{reflections.length === 5 ? "5+" : reflections.length}</div>
        </Card>
      </section>

      {/* Mood status */}
      {topMoods.length > 0 && (
        <section>
          <div className="mb-2.5 font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
            How you've been arriving
          </div>
          <div className="flex flex-wrap gap-2">
            {topMoods.map(([slug, count]) => {
              const mood = moodById(slug as MoodId);
              return mood ? (
                <div key={slug} className="flex items-center gap-1.5">
                  <MoodChip mood={mood} />
                  <span className="text-meta text-muted">×{count}</span>
                </div>
              ) : null;
            })}
          </div>
        </section>
      )}

      {/* Recent reflections preview */}
      <section>
        <div className="mb-2.5 flex items-baseline justify-between">
          <div className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
            Recent reflections
          </div>
          <Button size="sm" variant="mustard" leadingIcon={<Icon name="spark" size={14} />} onClick={openReflect}>
            Begin today&apos;s reset
          </Button>
        </div>

        {loading ? (
          <div className="space-y-2.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 w-full animate-pulse rounded-md bg-line/60" />
            ))}
          </div>
        ) : reflections.length === 0 ? (
          <Card className="text-center">
            <p className="font-serif text-h3 italic text-ink-soft">Your first reset is waiting.</p>
            <p className="mx-auto mt-1.5 max-w-sm text-small text-muted">
              No streaks to defend, nothing to catch up on. Just three quiet minutes whenever you&apos;re ready.
            </p>
            <Button className="mt-4" onClick={openReflect} trailingIcon={<Icon name="arrow" size={16} />}>
              Begin
            </Button>
          </Card>
        ) : (
          <div className="flex flex-col gap-2.5">
            {reflections.map((r) => {
              const mood = r.moodSlug ? moodById(r.moodSlug as MoodId) : undefined;
              return (
                <Card key={r.id} className="flex items-start gap-3">
                  {mood && <MoodChip mood={mood} />}
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-small text-ink">{r.preview || "A quiet, wordless check-in."}</p>
                    <div className="mt-1 text-meta text-muted">{relativeDay(r.createdAt)}{r.saved ? " · saved" : ""}</div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};
